import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { expenseList } from '../../api/endpoints';
import { getList } from '../../api/client';
import {
  spendByLine, spendByHead, fyOfExpense, expensesForLine, parseSplit, describeSplit, OTHER_LINE
} from '../expenses/budgetLines';
import type { Expense } from '../../types';
import { budgetHeads as budgetHeadsApi } from '../../api/endpoints';
import { useNotification } from '../../hooks';
import { formatCurrencyPrecise, formatDate } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  Button, ConfirmDialog, Input, Modal, Notification, Select, Icon
} from '../../components/ui';
import type { IconName } from '../../components/ui';
import { hexToRgba } from '../../theme/tokens';
import type { BudgetDocument as BudgetDoc, BudgetHead } from '../../types';
import {
  annualOf, cloneYear, enrichHeads, headLabel, itemLabel,
  nextFinancialYear, withDisplayOrder, documentProblems
} from './variance';

const newId = () => Math.random().toString(36).slice(2, 11);

const EMPTY_HEAD = (): BudgetHead => ({
  id: newId(), headName: 'New Budget Head', items: []
});

const EMPTY_ITEM = () => ({
  id: newId(), name: 'New Item', amount: 0, isOneTime: false, actual: 0
});

/**
 * The annual budget preparation document: numbered main heads (I, II, III) each
 * owning lettered sub-items (a, b, c), edited inline and saved as one tree.
 *
 * Sub-item amounts are entered per month unless flagged one-time — the /mo–/yr
 * toggle switches that, and the annual column recomputes from it.
 */
export interface BudgetDocumentProps {
  isDarkMode?: boolean;
  canEdit?: boolean;
  /** Told whenever there are (or no longer are) unsaved edits, so the parent can guard tab switches. */
  onDirtyChange?: (dirty: boolean) => void;
}

const BudgetDocument = ({ isDarkMode, canEdit = true, onDirtyChange }: BudgetDocumentProps) => {
  // Actual spend is COUNTED from the expenses booked against each line, never
  // typed. A stored figure drifts the moment an expense is voided or corrected;
  // counting means voiding one simply gives the money back.
  const [ledger, setLedger] = useState<Expense[]>([]);
  useEffect(() => {
    expenseList.page({ bucket: 'active', page: 0, size: 100000 })
      .then((res) => setLedger(getList<Expense>(res)))
      .catch(() => setLedger([]));
  }, []);
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [document, setDocument] = useState<BudgetDoc>({});
  const [currentYear, setCurrentYear] = useState('');

  /** The line whose entries are open, or null. */
  const [openLine, setOpenLine] = useState<{ head: string; item: string } | null>(null);
  const lineEntries = useMemo(() => {
    if (!openLine) return [];
    const inYear = ledger.filter((e) => fyOfExpense(e.date) === currentYear);
    if (openLine.item !== OTHER_LINE) return expensesForLine(inYear, openLine.head, openLine.item);
    // The head's expenses that match none of its named lines.
    const head = (document[currentYear] || []).find(
      (h) => String(h.headName || '').trim().toLowerCase() === openLine.head.trim().toLowerCase()
    );
    const names = new Set((head?.items || []).map((i) => String(i.name || '').trim().toLowerCase()));
    return inYear
      .filter((e) => e.status !== 'VOID')
      .filter((e) => String(e.category || '').trim().toLowerCase() === openLine.head.trim().toLowerCase())
      .filter((e) => !names.has(String(e.expenseName || '').trim().toLowerCase()))
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, [openLine, ledger, currentYear, document]);

  /** What each line of THIS year has been spent, keyed "head||item". */
  const spend = useMemo(
    () => spendByLine(ledger.filter((e) => fyOfExpense(e.date) === currentYear)),
    [ledger, currentYear]
  );
  const actualOf = useCallback(
    (headName?: string, itemName?: string) =>
      spend.get(`${String(headName || '').trim().toLowerCase()}||${String(itemName || '').trim().toLowerCase()}`) || 0,
    [spend]
  );

  // Spend under a head against no named line. It belongs to the head all the
  // same, so it is shown on its own read-only row and counted in the subtotal.
  const yearLedger = useMemo(() => ledger.filter((e) => fyOfExpense(e.date) === currentYear), [ledger, currentYear]);
  const headSpend = useMemo(() => spendByHead(yearLedger), [yearLedger]);
  const otherOf = useCallback((headName?: string) => {
    const k = String(headName || '').trim().toLowerCase();
    const head = (document[currentYear] || []).find((h) => String(h.headName || '').trim().toLowerCase() === k);
    const matched = (head?.items || []).reduce((sum, item) => sum + actualOf(headName, item.name), 0);
    return Math.max(0, (headSpend.get(k) || 0) - matched);
  }, [document, currentYear, headSpend, actualOf]);

  // Expense categories this year that are not heads of this budget at all.
  const unbudgeted = useMemo(() => {
    const heads = new Set((document[currentYear] || []).map((h) => String(h.headName || '').trim().toLowerCase()));
    const out = new Map<string, number>();
    yearLedger.forEach((e) => {
      if (e.status === 'VOID') return;
      const k = String(e.category || '').trim().toLowerCase();
      if (heads.has(k)) return;
      out.set(e.category || 'Uncategorised', (out.get(e.category || 'Uncategorised') || 0) + (parseFloat(String(e.amount)) || 0));
    });
    return Array.from(out.entries()).sort((a, b) => b[1] - a[1]);
  }, [document, currentYear, yearLedger]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** When the document was fetched — sent with the save so a stale copy is refused. */
  const [loadedAt, setLoadedAt] = useState('');
  /** Set when the server refused the save because someone else saved first. */
  const [conflict, setConflict] = useState('');

  // Unsaved edits: tell the parent (which guards its tab switch) and the
  // browser (which asks before a close or refresh).
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  useEffect(() => () => { onDirtyChange?.(false); }, [onDirtyChange]);

  const [newYearOpen, setNewYearOpen] = useState(false);
  const [newYearInput, setNewYearInput] = useState('');
  const [increasePercent, setIncreasePercent] = useState('0');
  const [pendingDeleteHead, setPendingDeleteHead] = useState<BudgetHead | null>(null);

  const { notification, notify, dismiss } = useNotification();

  const years = useMemo(() => Object.keys(document).sort().reverse(), [document]);
  // `document[currentYear] || []` allocates a fresh array whenever the year has
  // no heads, so the lookup lives inside the memo rather than feeding it.
  const { heads, grandTotalAnnual, grandTotalActual, variance } = useMemo(
    () => enrichHeads(document[currentYear] || [], actualOf, otherOf),
    [document, currentYear, actualOf, otherOf]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await budgetHeadsApi.get();
      const safe = data || {};
      setDocument(safe);
      setLoadedAt(new Date().toISOString());
      setConflict('');
      setCurrentYear((prev) => prev || Object.keys(safe).sort().reverse()[0] || '');
      setDirty(false);
    } catch (err) {
      notify(err.message || 'Could not load the budget document.', 'error');
    } finally {
      setLoading(false);
    }
    // `notify` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- Mutations. Every one marks the document dirty; nothing persists until Save. ---

  const setYearHeads = (updater: (heads: BudgetHead[]) => BudgetHead[]) => {
    setDocument((prev) => ({ ...prev, [currentYear]: updater(prev[currentYear] || []) }));
    setDirty(true);
  };

  const mapHead = (headId: string, fn: (head: BudgetHead) => BudgetHead) =>
    setYearHeads((list) => list.map((h) => (h.id === headId ? fn(h) : h)));

  const updateHeadName = (headId: string, headName: string) =>
    mapHead(headId, (h) => ({ ...h, headName }));

  const addHead = () => setYearHeads((list) => [...list, EMPTY_HEAD()]);

  const deleteHead = (headId: string) =>
    setYearHeads((list) => list.filter((h) => h.id !== headId));

  const addItem = (headId: string) =>
    mapHead(headId, (h) => ({ ...h, items: [...(h.items || []), EMPTY_ITEM()] }));

  const deleteItem = (headId: string, itemId: string) =>
    mapHead(headId, (h) => ({ ...h, items: (h.items || []).filter((i) => i.id !== itemId) }));

  const updateItem = (headId: string, itemId: string, field: string, value) =>
    mapHead(headId, (h) => ({
      ...h,
      items: (h.items || []).map((item) => (
        item.id === itemId
          ? { ...item, [field]: field === 'amount' || field === 'actual' ? Number(value) || 0 : value }
          : item
      ))
    }));

  /** Swaps a head with its neighbour; displayOrder is stamped on save. */
  const moveHead = (headId: string, direction: number) =>
    setYearHeads((list) => {
      const index = list.findIndex((h) => h.id === headId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= list.length) return list;
      const next = [...list];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const moveItem = (headId: string, itemId: string, direction: number) =>
    mapHead(headId, (head) => {
      const items = [...(head.items || [])];
      const index = items.findIndex((i) => i.id === itemId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= items.length) return head;
      [items[index], items[target]] = [items[target], items[index]];
      return { ...head, items };
    });

  // --- Save / new year ---

  const handleSave = async () => {
    // Every year is saved together, so every year has to be sound.
    const problems = Object.entries(document).flatMap(([fy, heads]) => documentProblems(fy, heads));
    if (problems.length) {
      notify(`${problems[0]}${problems.length > 1 ? ` (+${problems.length - 1} more)` : ''}`, 'error');
      return;
    }

    setSaving(true);
    try {
      const ordered = withDisplayOrder(document);
      await budgetHeadsApi.save(ordered, loadedAt);
      setDocument(ordered);
      setLoadedAt(new Date().toISOString());
      setConflict('');
      setDirty(false);
      notify('Budget document saved.', 'success');
    } catch (err) {
      if (err?.status === 409) setConflict(err.message);
      notify(err.message || 'Failed to save the budget document.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openNewYear = () => {
    setNewYearInput(nextFinancialYear(currentYear));
    setIncreasePercent('0');
    setNewYearOpen(true);
  };

  const createNewYear = () => {
    const year = newYearInput.trim();
    if (!year) { notify('Enter a financial year, e.g. 2027-2028.', 'error'); return; }
    if (document[year]) { notify(`${year} already exists.`, 'error'); return; }

    const percent = parseFloat(increasePercent) || 0;
    setDocument((prev) => ({ ...prev, [year]: cloneYear(prev[currentYear] || [], percent, newId) }));
    setCurrentYear(year);
    setDirty(true);
    setNewYearOpen(false);
    notify(
      percent !== 0
        ? `${year} created from ${currentYear} with a ${percent}% increase. Remember to Save.`
        : `${year} created from ${currentYear}. Remember to Save.`,
      'success'
    );
  };

  // --- Shared cell styling ---
  const cell = { padding: '8px', borderBottom: `1px solid ${t.border}`, fontSize: t.fontSize.md };
  const numCell = { ...cell, textAlign: 'right' as const, whiteSpace: 'nowrap' as const };
  const headRow = { backgroundColor: t.bgSubtle, fontWeight: t.fontWeight.bold };
  const iconBtn = {
    background: 'none', border: `1px solid ${t.border}`, borderRadius: t.radius.sm,
    cursor: 'pointer', padding: '2px 6px', color: t.textMuted, fontSize: '11px'
  };
  /**
   * The row actions as round SVG icon buttons with the name on hover — the
   * same buttons as Budget Preparation, drawn from the app's icon set rather
   * than emoji, which ignore the button's colour and vary by machine.
   */
  const actionButton = (icon: IconName, label: string, onClick: () => void, tone: string = t.textMuted) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', marginLeft: '4px',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle',
        color: tone, backgroundColor: hexToRgba(tone, t.isDarkMode ? 0.22 : 0.12),
        border: `1px solid ${hexToRgba(tone, 0.45)}`
      }}
    >
      <Icon name={icon} size={14} strokeWidth={2.4} />
    </button>
  );

  // --- Print preview takes over the screen ---

  return (
    <div>
      <Notification notification={notification} onClose={dismiss} />

      {/* Every expense behind one line's Actual figure, so the total can be
          checked against the receipts rather than taken on trust. */}
      {openLine && (
        <Modal
          open
          isDarkMode={isDarkMode}
          width="820px"
          title={`${openLine.head} — ${openLine.item}`}
          onClose={() => setOpenLine(null)}
          footer={<Button variant="secondary" onClick={() => setOpenLine(null)}>Close</Button>}
        >
          <p style={{ marginTop: 0, color: t.textMuted }}>
            {lineEntries.length} expense{lineEntries.length === 1 ? '' : 's'} booked against this
            line in {currentYear}. Voided entries are not counted.
          </p>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: t.fontSize.base }}>
              <thead>
                <tr>
                  {['Date', 'Voucher', 'Paid To', 'Mode', 'Remarks', 'Amount'].map((h, i) => (
                    <th key={h} style={{
                      textAlign: i === 5 ? 'right' : 'left', padding: '8px 10px',
                      borderBottom: `2px solid ${t.border}`, color: t.textMuted,
                      fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap'
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineEntries.map((e) => {
                  const parts = parseSplit(e.splitPayments);
                  return (
                    <tr key={e.id}>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
                        {formatDate(e.date)}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
                        {e.internalId || '-'}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}` }}>{e.paidTo || '-'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
                        {e.paymentMode || '-'}
                        {parts.length > 0 && (
                          <div style={{ fontSize: '11px', color: t.textFaint }}>{describeSplit(parts)}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, color: t.textMuted }}>
                        {e.remarks || '-'}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        {formatCurrencyPrecise(e.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: t.textMain }}>
                    Total spent on this line
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 900, color: t.expense, whiteSpace: 'nowrap' }}>
                    {formatCurrencyPrecise(openLine.item === OTHER_LINE ? otherOf(openLine.head) : actualOf(openLine.head, openLine.item))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Modal>
      )}

      {/* --- Year selector + document actions --- */}
      <div style={{
        display: 'flex', gap: t.spacing.md, alignItems: 'flex-end',
        flexWrap: 'wrap', marginBottom: t.spacing.lg
      }}>
        {years.length > 0 && (
          <Select
            label="Financial Year"
            placeholder=""
            options={years.map((y) => ({ value: y, label: `FY ${y}` }))}
            value={currentYear}
            onChange={(e) => setCurrentYear(e.target.value)}
            containerStyle={{ marginBottom: 0, minWidth: '160px' }}
          />
        )}
        {canEdit && (
          <>
            <Button write variant="secondary" onClick={openNewYear} disabled={!currentYear}>
              + New Financial Year
            </Button>
            <Button write variant="secondary" onClick={addHead} disabled={!currentYear}>
              + Add Budget Head
            </Button>
            <Button write onClick={handleSave} disabled={saving || !dirty}>
              {saving ? 'Saving…' : dirty ? '💾 Save Changes' : 'Saved'}
            </Button>
          </>
        )}
      </div>

      {conflict && (
        <div style={{
          display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap',
          padding: '12px 16px', marginBottom: t.spacing.md, borderRadius: t.radius.lg,
          backgroundColor: t.isDarkMode ? 'rgba(211,47,47,0.18)' : '#FDECEA', border: `1px solid ${t.danger}`, color: t.textMain
        }}>
          <span style={{ flex: 1 }}>⚠ {conflict}</span>
          <Button variant="secondary" size="sm" onClick={load}>↻ Reload document</Button>
        </div>
      )}

      {unbudgeted.length > 0 && (
        <div style={{
          padding: '10px 14px', marginBottom: t.spacing.md, borderRadius: t.radius.lg,
          backgroundColor: t.bgSubtle, border: `1px dashed ${t.warning}`, color: t.textMuted, fontSize: t.fontSize.sm
        }}>
          <strong style={{ color: t.textMain }}>Not in this budget:</strong>{' '}
          {formatCurrencyPrecise(unbudgeted.reduce((sum, [, v]) => sum + v, 0))} of FY {currentYear} expenses
          sit under categories that are not heads here —{' '}
          {unbudgeted.map(([name, value]) => `${name} (${formatCurrencyPrecise(value)})`).join(', ')}.
          Add them as heads, or re-file those expenses, for the document to account for every rupee.
        </div>
      )}

      {dirty && (
        <div style={{
          padding: t.spacing.sm, marginBottom: t.spacing.md, borderRadius: t.radius.sm,
          backgroundColor: t.bgSubtle, color: t.warning, fontSize: t.fontSize.md,
          fontWeight: t.fontWeight.bold
        }}>
          Unsaved changes — click Save Changes to write them to the database.
        </div>
      )}

      {/* --- Totals --- */}
      <div style={{ display: 'flex', gap: t.spacing.lg, marginBottom: t.spacing.lg, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Annual Budget', value: grandTotalAnnual, accent: t.info },
          { label: 'Total Actual Expenses', value: grandTotalActual, accent: t.danger },
          { label: 'Remaining Variance', value: variance, accent: variance < 0 ? t.danger : t.income }
        ].map((box) => (
          <div key={box.label} style={{
            flex: '1 1 200px', backgroundColor: t.bgSubtle, padding: '15px',
            borderRadius: t.radius.lg, borderLeft: `4px solid ${box.accent}`
          }}>
            <div style={{ fontSize: '12px', color: box.accent, fontWeight: 'bold', textTransform: 'uppercase' }}>
              {box.label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: 900, color: t.textMain }}>
              {formatCurrencyPrecise(box.value)}
            </div>
          </div>
        ))}
      </div>

      {/* --- The editable grid --- */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: t.textFaint }}>Loading…</div>
      ) : !currentYear ? (
        <div style={{
          padding: '40px 20px', textAlign: 'center', backgroundColor: t.bgSubtle,
          border: `1px dashed ${t.borderStrong}`, borderRadius: '8px', color: t.textFaint
        }}>
          No budget document yet.
          {canEdit && ' Create the first financial year to begin.'}
          {canEdit && (
            <div style={{ marginTop: t.spacing.md }}>
              <Button write onClick={() => { setNewYearInput(''); setIncreasePercent('0'); setNewYearOpen(true); }}>
                + Create Financial Year
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px', color: t.textMain }}>
            <thead>
              <tr style={{ backgroundColor: t.primary, color: t.textOnPrimary }}>
                <th style={{ ...cell, textAlign: 'left', width: '34%' }}>Particulars (Head / Sub-item)</th>
                <th style={{ ...cell, textAlign: 'right' }}>Allotment</th>
                <th style={{ ...cell, textAlign: 'center' }}>Per</th>
                <th style={{ ...cell, textAlign: 'right' }}>Annual Budget</th>
                <th style={{ ...cell, textAlign: 'right' }}>Actual</th>
                <th style={{ ...cell, textAlign: 'right' }}>% of Total</th>
                {canEdit && <th style={{ ...cell, textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {heads.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 7 : 6} style={{ ...cell, textAlign: 'center', color: t.textFaint, padding: '30px' }}>
                    No budget heads for FY {currentYear}.{canEdit && ' Use “+ Add Budget Head”.'}
                  </td>
                </tr>
              )}

              {heads.map((head, index) => (
                <React.Fragment key={head.id}>
                  <tr style={headRow}>
                    <td style={cell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: t.textMuted, minWidth: '32px' }}>{headLabel(index)}.</span>
                        {canEdit ? (
                          <input
                            value={head.headName || ''}
                            onChange={(e) => updateHeadName(head.id, e.target.value)}
                            style={{
                              flex: 1, background: 'transparent', border: 'none',
                              borderBottom: `1px dashed ${t.border}`, color: t.textMain,
                              fontWeight: t.fontWeight.bold, fontSize: t.fontSize.md, padding: '4px 0'
                            }}
                          />
                        ) : <strong>{head.headName}</strong>}
                      </div>
                    </td>
                    <td style={numCell}>-</td>
                    <td style={{ ...cell, textAlign: 'center' }}>-</td>
                    <td style={{ ...numCell, color: t.info }}>{formatCurrencyPrecise(head.headAnnual)}</td>
                    <td style={{ ...numCell, color: t.expense }}>{formatCurrencyPrecise(head.headActual)}</td>
                    <td style={numCell}>{head.percentOfTotal.toFixed(1)}%</td>
                    {canEdit && (
                      <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {actionButton('chevronUp', 'Move head up', () => moveHead(head.id, -1))}
                        {actionButton('chevronDown', 'Move head down', () => moveHead(head.id, 1))}
                        {actionButton('plus', 'Add sub-item', () => addItem(head.id), t.success)}
                        {actionButton('trash', 'Delete head', () => setPendingDeleteHead(head), t.danger)}
                      </td>
                    )}
                  </tr>

                  {(head.items || []).map((item, subIndex) => (
                    <tr key={item.id}>
                      <td style={{ ...cell, paddingLeft: '38px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: t.textFaint }}>({itemLabel(subIndex)})</span>
                          {canEdit ? (
                            <input
                              value={item.name || ''}
                              onChange={(e) => updateItem(head.id, item.id, 'name', e.target.value)}
                              style={{
                                flex: 1, background: 'transparent', border: 'none',
                                borderBottom: `1px dashed ${t.border}`, color: t.textMain,
                                fontSize: t.fontSize.md, padding: '4px 0'
                              }}
                            />
                          ) : item.name}
                        </div>
                      </td>
                      <td style={numCell}>
                        {canEdit ? (
                          <input
                            type="number"
                            value={item.amount ?? 0}
                            onChange={(e) => updateItem(head.id, item.id, 'amount', e.target.value)}
                            style={{
                              width: '110px', textAlign: 'right', background: 'transparent',
                              border: `1px solid ${t.border}`, borderRadius: t.radius.sm,
                              color: t.textMain, padding: '4px 6px'
                            }}
                          />
                        ) : formatCurrencyPrecise(item.amount)}
                      </td>
                      <td style={{ ...cell, textAlign: 'center' }}>
                        {/* One-time lines are already annual; monthly lines are x12. */}
                        <button
                          type="button"
                          disabled={!canEdit}
                          onClick={() => updateItem(head.id, item.id, 'isOneTime', !item.isOneTime)}
                          title={item.isOneTime ? 'One-time / yearly amount' : 'Monthly amount (x12)'}
                          style={{
                            ...iconBtn,
                            cursor: canEdit ? 'pointer' : 'default',
                            color: item.isOneTime ? t.warning : t.info,
                            fontWeight: t.fontWeight.bold,
                            minWidth: '46px'
                          }}
                        >
                          {item.isOneTime ? '/ yr' : '/ mo'}
                        </button>
                      </td>
                      <td style={numCell}>{formatCurrencyPrecise(annualOf(item))}</td>
                      {/* Counted from the expenses booked against this line, so it
                          cannot disagree with the ledger and needs no upkeep.
                          A total nobody can open is a total nobody can check, so
                          it opens the entries that make it up. */}
                      <td style={numCell}>
                        {(() => {
                          const spent = actualOf(head.headName, item.name);
                          if (!spent) return formatCurrencyPrecise(0);
                          return (
                            <button
                              type="button"
                              onClick={() => setOpenLine({ head: head.headName || '', item: item.name || '' })}
                              title="Show the expenses behind this figure"
                              style={{
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                color: t.info, fontWeight: 'bold', textDecoration: 'underline',
                                fontSize: 'inherit', fontFamily: 'inherit'
                              }}
                            >
                              {formatCurrencyPrecise(spent)}
                            </button>
                          );
                        })()}
                      </td>
                      <td style={numCell}>
                        {(() => {
                          const left = annualOf(item) - actualOf(head.headName, item.name);
                          return (
                            <span style={{ fontWeight: 'bold', color: left < 0 ? t.danger : t.income }}>
                              {formatCurrencyPrecise(left)}
                            </span>
                          );
                        })()}
                      </td>
                      {canEdit && (
                        <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {actionButton('chevronUp', 'Move line up', () => moveItem(head.id, item.id, -1))}
                          {actionButton('chevronDown', 'Move line down', () => moveItem(head.id, item.id, 1))}
                          {actionButton('trash', 'Delete line', () => deleteItem(head.id, item.id), t.danger)}
                        </td>
                      )}
                    </tr>
                  ))}
                  {/* Spend booked under this head against no named line. Read-only:
                      it is what the expense register says, and it keeps the head
                      subtotal above equal to it. */}
                  {head.headOther > 0 && (
                    <tr>
                      <td style={{ ...cell, paddingLeft: '30px', color: t.textMuted, fontStyle: 'italic' }}>
                        {OTHER_LINE}
                        <button
                          type="button"
                          onClick={() => setOpenLine({ head: head.headName || '', item: OTHER_LINE })}
                          style={{ ...iconBtn, marginLeft: '8px' }}
                          title="Show these expenses"
                        >
                          entries
                        </button>
                      </td>
                      <td style={numCell}>—</td>
                      <td style={{ ...cell, textAlign: 'center' }}>—</td>
                      <td style={numCell}>—</td>
                      <td style={{ ...numCell, color: t.expense }}>{formatCurrencyPrecise(head.headOther)}</td>
                      <td style={{ ...numCell, color: t.danger, fontWeight: 'bold' }}>{formatCurrencyPrecise(-head.headOther)}</td>
                      {canEdit && <td style={cell} />}
                    </tr>
                  )}
                </React.Fragment>
              ))}

              <tr style={{ backgroundColor: t.bgSubtle, fontWeight: 900 }}>
                <td style={{ ...cell, fontSize: t.fontSize.base }}>GRAND TOTAL</td>
                <td style={numCell}>-</td>
                <td style={{ ...cell, textAlign: 'center' }}>-</td>
                <td style={{ ...numCell, color: t.info, fontSize: t.fontSize.base }}>{formatCurrencyPrecise(grandTotalAnnual)}</td>
                <td style={{ ...numCell, color: t.expense, fontSize: t.fontSize.base }}>{formatCurrencyPrecise(grandTotalActual)}</td>
                <td style={numCell}>100%</td>
                {canEdit && <td style={cell} />}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* --- New financial year --- */}
      <Modal
        open={newYearOpen}
        title="Create New Financial Year"
        onClose={() => setNewYearOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setNewYearOpen(false)}>Cancel</Button>
            <Button write onClick={createNewYear}>Create Year</Button>
          </>
        }
      >
        <p style={{ color: t.textMuted, fontSize: t.fontSize.md, marginTop: 0 }}>
          {currentYear
            ? `Copies every head and sub-item from FY ${currentYear}. Actuals reset to zero.`
            : 'Creates an empty financial year.'}
        </p>
        <Input
          label="New Financial Year" placeholder="e.g. 2027-2028"
          value={newYearInput}
          onChange={(e) => setNewYearInput(e.target.value)}
        />
        <Input
          label="Auto-increase every amount by (%)" type="number" placeholder="0"
          value={increasePercent}
          onChange={(e) => setIncreasePercent(e.target.value)}
          hint="Leave at 0 to copy the amounts unchanged."
        />
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDeleteHead)}
        title="Delete Budget Head"
        message={`Delete “${pendingDeleteHead?.headName}” and all of its sub-items? This takes effect when you save.`}
        confirmLabel="Delete Head"
        onConfirm={() => {
          if (pendingDeleteHead) deleteHead(pendingDeleteHead.id);
          setPendingDeleteHead(null);
        }}
        onCancel={() => setPendingDeleteHead(null)}
      />
    </div>
  );
};

export default BudgetDocument;
